import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { apiRequest } from '../../utils/apiClient';
import { useDeveloper } from '../../contexts/DeveloperContext';

interface DashboardData {
  developer: { id: string; name: string; nameAr?: string | null; slug: string; logoUrl?: string | null; isVerified?: boolean };
  myRole: string;
  stats: { projects: number; activeListings: number; teamCount: number; openInvest: number; leads: number };
}

export default function DeveloperDashboardPage() {
  const { i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  const t = (en: string, ar: string) => (isRTL ? ar : en);
  const { developer } = useDeveloper();
  const [data, setData] = useState<DashboardData | null>(null);

  useEffect(() => {
    apiRequest<{ data: DashboardData | null }>('/developers/my/dashboard')
      .then((res) => setData(res.data))
      .catch(() => setData(null));
  }, []);

  const name = isRTL ? (developer?.nameAr || developer?.name) : developer?.name;
  const s = data?.stats;
  const cards = [
    { label: t('Projects', 'المشاريع'), value: s?.projects ?? 0, icon: 'ri-building-4-line' },
    { label: t('Active units', 'وحدات معروضة'), value: s?.activeListings ?? 0, icon: 'ri-home-4-line' },
    { label: t('Team', 'الفريق'), value: s?.teamCount ?? 0, icon: 'ri-team-line' },
    { label: t('Open to invest', 'فرص استثمار'), value: s?.openInvest ?? 0, icon: 'ri-funds-line' },
    { label: t('Investor leads', 'طلبات المستثمرين'), value: s?.leads ?? 0, icon: 'ri-mail-line' },
  ];

  return (
    <div className="min-h-screen bg-white" dir={i18n.dir()}>
      <Navigation />
      <main className="mx-auto max-w-6xl px-4 py-8">
        <div className={`mb-6 flex items-center gap-4 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
          {developer?.logoUrl ? (
            <img src={developer.logoUrl} alt={name || ''} className="h-14 w-14 rounded-xl border border-slate-100 bg-white object-cover" />
          ) : (
            <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-[#004D40] text-xl font-bold text-white">{(name || 'D').charAt(0)}</div>
          )}
          <div>
            <h1 className="flex items-center gap-2 text-xl font-bold text-[var(--pv-color-primary)]">
              {name}{developer?.isVerified && <i className="ri-verified-badge-fill text-[#004D40]" />}
            </h1>
            <p className="text-sm text-slate-500">{t('Developer dashboard', 'لوحة تحكم المطوّر')}</p>
          </div>
        </div>

        <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {cards.map((c) => (
            <div key={c.label} className={`rounded-2xl border border-slate-200/80 bg-white p-4 ${isRTL ? 'text-right' : ''}`}>
              <div className={`mb-1 flex items-center gap-2 text-xs text-slate-400 ${isRTL ? 'flex-row-reverse' : ''}`}><i className={c.icon} />{c.label}</div>
              <div className="text-xl font-semibold text-slate-900">{c.value}</div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Link to="/developer/projects" className="rounded-2xl border border-slate-200/80 bg-white p-5 hover:border-[var(--pv-color-accent-soft)] transition-colors">
            <i className="ri-building-4-line text-2xl text-[#004D40]" />
            <h3 className="mt-2 font-semibold text-slate-900">{t('Projects & schemes', 'المشاريع والمخططات')}</h3>
            <p className="text-sm text-slate-500">{t('Create developments and land schemes, then add their units.', 'أنشئ المشاريع والمخططات ثم أضف وحداتها.')}</p>
          </Link>
          <Link to="/developer/team" className="rounded-2xl border border-slate-200/80 bg-white p-5 hover:border-[var(--pv-color-accent-soft)] transition-colors">
            <i className="ri-team-line text-2xl text-[#004D40]" />
            <h3 className="mt-2 font-semibold text-slate-900">{t('Manage team', 'إدارة الفريق')}</h3>
            <p className="text-sm text-slate-500">{t('Invite members and assign roles.', 'ادعُ الأعضاء وحدّد الأدوار.')}</p>
          </Link>
          <Link to="/developer/settings" className="rounded-2xl border border-slate-200/80 bg-white p-5 hover:border-[var(--pv-color-accent-soft)] transition-colors">
            <i className="ri-settings-3-line text-2xl text-[#004D40]" />
            <h3 className="mt-2 font-semibold text-slate-900">{t('Settings', 'الإعدادات')}</h3>
            <p className="text-sm text-slate-500">{t('Edit your developer profile.', 'عدّل ملف المطوّر.')}</p>
          </Link>
          {developer?.slug && (
            <Link to={`/developers/${developer.slug}`} className="rounded-2xl border border-slate-200/80 bg-white p-5 hover:border-[var(--pv-color-accent-soft)] transition-colors">
              <i className="ri-external-link-line text-2xl text-[#004D40]" />
              <h3 className="mt-2 font-semibold text-slate-900">{t('Public profile', 'الملف العام')}</h3>
              <p className="text-sm text-slate-500">{t('View your public developer page.', 'اعرض صفحتك العامة.')}</p>
            </Link>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
